import { useTranslation } from "react-i18next"

import type { HostedAgentFormState } from "../utils/hosted-agents"
import type { HostedAgent } from "@/api/admin-api"

import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

/** Publication is a deliberate, revision-guarded save, never a side effect of setup. */
export function HostedAgentPublishTab({
  agent,
  form,
  patch,
}: {
  agent: HostedAgent
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
}) {
  const { t } = useTranslation()
  const text = (key: string) => t(`adminDashboard.hostedAgents.product.${key}`)
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">{text("reviewBeforePublish")}</h3>
        <p className="text-muted-foreground mt-1 text-sm">{text("publishHelp")}</p>
      </div>
      <dl className="grid gap-3 rounded-md border p-4 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">{text("name")}</dt>
          <dd>{form.name || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{text("role")}</dt>
          <dd>{form.title || "—"}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{text("steps.knowledge")}</dt>
          <dd>
            {t("adminDashboard.hostedAgents.product.libraryCount", {
              count: form.libraries.length,
            })}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{text("tools")}</dt>
          <dd>
            {t("adminDashboard.hostedAgents.product.toolCount", { count: form.tools.length })}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{text("monthlyLimit")}</dt>
          <dd>{form.budgetMonthlySek} SEK</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{text("newChatVisibility")}</dt>
          <dd>{text(form.memoryMode === "open" ? "shared" : "personal")}</dd>
        </div>
      </dl>
      <p className="text-muted-foreground text-sm">{text("permissionsReminder")}</p>
      <div className="flex items-center justify-between gap-4 rounded-md border p-4">
        <div className="space-y-1">
          <Label htmlFor="hosted-agent-enabled">{text("publishEnable")}</Label>
          <p className="text-muted-foreground text-xs">{text("publishEnableHelp")}</p>
        </div>
        <Switch
          checked={form.enabled}
          id="hosted-agent-enabled"
          onCheckedChange={(enabled) => patch({ enabled })}
        />
      </div>
      <p className="text-muted-foreground text-sm" role="status">
        {text(agent.enabled ? "currentlyPublished" : "currentlyDraft")}
      </p>
      <p className="text-muted-foreground text-sm">{text("routineHint")}</p>
    </div>
  )
}
